import { useState, useCallback } from 'react';
import { Key, Copy, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const PasswordGenerator = () => {
  useToolUsage('password_generator'); // Track tool usage

  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [copied, setCopied] = useState(false);

  const generatePassword = useCallback(() => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    let charset = '';
    if (includeUppercase) charset += uppercase;
    if (includeLowercase) charset += lowercase;
    if (includeNumbers) charset += numbers;
    if (includeSymbols) charset += symbols;

    if (!charset) {
      toast.error('Please select at least one character type');
      return;
    }

    let newPassword = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      newPassword += charset[array[i] % charset.length];
    }

    setPassword(newPassword);
    setCopied(false);
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  const copyPassword = () => {
    if (password) {
      navigator.clipboard.writeText(password);
      setCopied(true);
      toast.success('Password copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const calculateStrength = () => {
    if (!password) return { score: 0, label: 'None', color: 'bg-muted' };

    let score = 0;
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 20;
    if (password.length >= 16) score += 10;
    if (/[a-z]/.test(password)) score += 15;
    if (/[A-Z]/.test(password)) score += 15;
    if (/[0-9]/.test(password)) score += 10;
    if (/[^a-zA-Z0-9]/.test(password)) score += 10;

    if (score < 30) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score < 50) return { score, label: 'Fair', color: 'bg-orange-500' };
    if (score < 70) return { score, label: 'Good', color: 'bg-yellow-500' };
    if (score < 90) return { score, label: 'Strong', color: 'bg-green-500' };
    return { score: 100, label: 'Very Strong', color: 'bg-emerald-500' };
  };

  const strength = calculateStrength();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Key className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Password Generator</h1>
          <p className="text-muted-foreground">
            Generate secure random passwords
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle>Generate Password</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Password Display */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={password}
                  readOnly
                  placeholder="Click 'Generate' to create a password"
                  className="font-mono text-lg bg-muted/30"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyPassword}
                  disabled={!password}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button onClick={generatePassword} className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Generate
                </Button>
              </div>

              {/* Strength Indicator */}
              {password && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Strength:</span>
                    <span className={cn(
                      "font-medium",
                      strength.score < 50 ? "text-red-500" :
                        strength.score < 70 ? "text-yellow-500" : "text-green-500"
                    )}>
                      {strength.label}
                    </span>
                  </div>
                  <Progress
                    value={strength.score}
                    className="h-2"
                  />
                </div>
              )}
            </div>

            {/* Length Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Password Length</Label>
                <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                  {length}
                </span>
              </div>
              <Slider
                value={[length]}
                onValueChange={(value) => setLength(value[0])}
                min={8}
                max={64}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>8</span>
                <span>64</span>
              </div>
            </div>

            {/* Character Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <Label htmlFor="uppercase" className="cursor-pointer">
                  Uppercase (A-Z)
                </Label>
                <Switch
                  id="uppercase"
                  checked={includeUppercase}
                  onCheckedChange={setIncludeUppercase}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <Label htmlFor="lowercase" className="cursor-pointer">
                  Lowercase (a-z)
                </Label>
                <Switch
                  id="lowercase"
                  checked={includeLowercase}
                  onCheckedChange={setIncludeLowercase}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <Label htmlFor="numbers" className="cursor-pointer">
                  Numbers (0-9)
                </Label>
                <Switch
                  id="numbers"
                  checked={includeNumbers}
                  onCheckedChange={setIncludeNumbers}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <Label htmlFor="symbols" className="cursor-pointer">
                  Symbols (!@#$...)
                </Label>
                <Switch
                  id="symbols"
                  checked={includeSymbols}
                  onCheckedChange={setIncludeSymbols}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordGenerator;